import { ConnectPaymentAccount } from '../data-models/connect-payment-account.model';
import { CustomerPaymentAccount } from '../data-models/customer-payment-account.model';
import { MembershipPlan } from '../data-models/membership-plan.model';
import { OrganizationMembershipSummary } from '../data-models/organization-membership-summary.model';
import { OrganizationPaymentAccounts } from '../data-models/organization-payment-accounts.model';
import { isRecord } from './is-record';
import { nullableString } from './wire-value-utils';

/**
 * Readers for the nested blocks of the admin organization detail. Each copies named fields only and is
 * read-only (never sent back), so missing values become `null` / `false`.
 */

/** The `membership` block: `undefined` when absent (list rows), `null` when the hub sent `null`. */
export function toMembershipSummary(value: unknown): OrganizationMembershipSummary | null | undefined {
  if (value === undefined || value === null) {
    return value;
  }
  const source = isRecord(value) ? value : {};
  return {
    tier: (source['tier'] === 'pro' ? 'pro' : 'core') as MembershipPlan,
    status: stringOrNull(source['status']),
    currentPeriodEnd: stringOrNull(source['currentPeriodEnd']),
    cancelAtPeriodEnd: source['cancelAtPeriodEnd'] === true,
    complimentary: source['complimentary'] === true,
    reason: stringOrNull(source['reason']),
  };
}

/** The `paymentAccounts` block, or `undefined` when the hub didn't send one. */
export function toPaymentAccounts(value: unknown): OrganizationPaymentAccounts | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  return { customer: toCustomerAccount(value['customer']), connect: toConnectAccount(value['connect']) };
}

function toCustomerAccount(value: unknown): CustomerPaymentAccount | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    stripeCustomerId: stringOrNull(value['stripeCustomerId']),
    status: stringOrNull(value['status']),
    defaultPaymentMethodId: stringOrNull(value['defaultPaymentMethodId']),
    subscriptionStatus: stringOrNull(value['subscriptionStatus']),
    subscriptionCurrentPeriodEnd: stringOrNull(value['subscriptionCurrentPeriodEnd']),
  };
}

function toConnectAccount(value: unknown): ConnectPaymentAccount | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    stripeAccountId: stringOrNull(value['stripeAccountId']),
    status: stringOrNull(value['status']),
    chargesEnabled: value['chargesEnabled'] === true,
    payoutsEnabled: value['payoutsEnabled'] === true,
    detailsSubmitted: value['detailsSubmitted'] === true,
  };
}

function stringOrNull(value: unknown): string | null {
  return nullableString(value) ?? null;
}
